import { useState } from 'react';

import { Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Combobox } from '@opendoor/atoms/combobox';
import { Field } from '@opendoor/atoms/field';
import { Image } from '@opendoor/atoms/image';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { IconCheck } from '@tabler/icons-react';
import type { ILpComponentOpendoorReviews } from 'declarations/contentful';

import { LandingPageSectionContainer } from '../shared/Container';
import { commentParts, fetchReviews } from './api';
import type { FeedItem, Review, ReviewsIOFeedItem } from './api';

enum ProductType {
  buy = 'buy',
  sell = 'sell',
  tradeIn = 'trade-in',
}

const FILTER_OPTIONS = [
  {
    label: 'Sold to Opendoor',
    value: ProductType.sell,
  },
  {
    label: 'Bought with Opendoor',
    value: ProductType.buy,
  },
  {
    label: 'Bought and sold with Opendoor',
    value: ProductType.tradeIn,
  },
];

const productTypeToExplainer: {
  [key in Review['productData']['productType']]: { heading: string; description: string };
} = {
  sell: {
    heading: 'Seller',
    description: 'These reviews are from customers that sold their home directly to Opendoor.',
  },
  buy: {
    heading: 'Buyer',
    description: 'These reviews are from customers that bought their home with Opendoor.',
  },
  'trade-in': {
    heading: 'Trade-in',
    description:
      'These reviews are from customers that sold their home directly to Opendoor and purchased their new home at the same time.',
  },
};

const typeToProduct: { [key in Review['productData']['productType']]: string } = {
  buy: 'Buyer',
  sell: 'Seller',
  'trade-in': 'Buyer and seller',
};

const typeToReviewContext: { [key in Review['productData']['productType']]: string } = {
  buy: 'Bought with Opendoor',
  sell: 'Sold to Opendoor',
  'trade-in': 'Bought and sold with Opendoor',
};

const STAR_RATING_URI =
  'https://images.opendoor.com/source/s3/imgdrop-production/2024-11-1732263853253-76957.svg?service=cosmos';
const STAR_RATING_SOURCE = {
  src: STAR_RATING_URI,
  width: 16,
  height: 16,
};

const ThirdPartyVerification = () => (
  <Col className="gap-6 rounded-3xl bg-od-bg-secondary px-5 py-8 md:px-28">
    <Col className="items-center justify-center gap-2 md:flex-row">
      <Text className="text-subheader-sm text-od-content-primary">Third Party</Text>
      <Row className="items-center gap-2">
        <Text className="text-label-md text-od-content-accent-brand">Verified Review</Text>
        <IconCheck size={20} className="text-od-content-accent-brand" aria-hidden="true" />
      </Row>
    </Col>
    <Text className="text-center text-body-lg text-od-content-primary">
      All reviews published on this page were submitted to a 3rd party review site. Each review has
      been been verified using additional trust signals to ensure it is genuine/authentic.
    </Text>
  </Col>
);

const ReviewsIoCard = ({ reviewsIo }: { reviewsIo: ReviewsIOFeedItem }) => (
  <Col className="gap-6 rounded-3xl bg-od-bg-secondary py-8 md:flex-row md:px-28">
    <Col className="flex-1 items-center justify-around gap-4">
      <Image
        alt="Reviews io logo"
        src="https://images.opendoor.com/source/s3/imgdrop-production/2024-11-1732437828073-65898.png?service=cosmos"
        width={175}
        height={28}
      />
      <Row>
        {Array.from({ length: 5 }).map((_val, index) => (
          <Image key={index} alt="Star Rating" {...STAR_RATING_SOURCE} />
        ))}
      </Row>
      <Text className="text-body-sm text-od-content-primary">
        {reviewsIo.stats?.averageRating} Rating | {reviewsIo.stats?.totalReviews} reviews
      </Text>
    </Col>
    <Col className="flex-1 items-center justify-around gap-4">
      <Text className="text-center text-header-sm text-od-content-primary">
        {reviewsIo.stats?.recommendedPercent}%
      </Text>
      <Text className="text-center text-body-sm text-od-content-primary">
        of reviewers recommend Opendoor
      </Text>
      <Button
        variant="secondaryInverse"
        size="sm"
        analyticsName="reviews-io-link"
        href="https://www.reviews.io/company-reviews/store/opendoor-com"
        external
        aria-label="See reviews.io"
      >
        See reviews.io
      </Button>
    </Col>
  </Col>
);

const BbbOrgCard = () => (
  <Col className="gap-6 rounded-3xl bg-od-bg-secondary py-8 md:flex-row md:px-28">
    <Col className="flex-1 items-center justify-center gap-4">
      <Image
        alt="BBB logo"
        src="https://images.opendoor.com/source/s3/imgdrop-production/2024-11-1732441222067-81668.png?service=cosmos"
        width={175}
        height={54}
      />
      <Text className="text-body-sm text-od-content-primary">Accredited since: 10/15/2015</Text>
    </Col>
    <Col className="flex-1 items-center justify-around gap-4">
      {/* BBB brand teal — no Atoms semantic token maps to this third-party color */}
      <Text className="text-center text-header-sm text-[#015A78]">A+</Text>
      <Text className="text-center text-body-sm text-od-content-primary">BBB rating</Text>
      <Button
        variant="secondaryInverse"
        size="sm"
        analyticsName="BBB Link Button"
        href="https://www.bbb.org/us/az/tempe/profile/real-estate-services/opendoor-1126-1000060421/customer-reviews"
        external
        aria-label="Visit BBB.org"
        className="bg-[#015A78]"
      >
        Visit BBB.org
      </Button>
    </Col>
  </Col>
);

const VerifiedReview = () => (
  <Row className="shrink-0 items-center gap-1">
    <Text className="text-label-sm text-od-content-accent-brand">Verified Review</Text>
    <IconCheck size={20} className="text-od-content-accent-brand" aria-hidden="true" />
  </Row>
);

export const ReviewCard = ({ review }: { review: Review }) => {
  const { heading, body, ellipsize } = commentParts(review.reviewComment);
  const reviewerName = review.reviewer.name?.trim();
  const cityContext = review.productData.city ? ` in ${review.productData.city}` : '';
  const reviewerContext = reviewerName
    ? `${typeToReviewContext[review.productData.productType]}${cityContext}`
    : `${typeToProduct[review.productData.productType]}${cityContext}`;
  const ratingLabel = `${review.rating} out of 5 stars`;

  const renderRatings = () => (
    // eslint-disable-next-line jsx-a11y/prefer-tag-over-role -- composite of star images announced as a single rating image
    <Row role="img" aria-label={ratingLabel}>
      {Array.from({ length: review.rating }).map((_val, index) => (
        <Image key={index} alt="" {...STAR_RATING_SOURCE} />
      ))}
    </Row>
  );

  return (
    <Col className="min-w-0 gap-4 rounded-2xl border border-od-border-primary bg-od-bg-primary px-5 py-5 md:px-7 md:py-6">
      <Row className="items-start justify-between gap-3">
        <Col className="min-w-0 gap-1.5">
          {renderRatings()}
          {reviewerName && (
            <Text className="text-body-lg font-medium text-od-content-primary">{reviewerName}</Text>
          )}
          <Text className="text-body-sm text-od-content-secondary">{reviewerContext}</Text>
        </Col>
        <VerifiedReview />
      </Row>

      <Col className="min-w-0 gap-2">
        <Text
          as="h3"
          className="font-medium text-body-xl text-od-content-primary [overflow-wrap:anywhere]"
        >
          {heading}
          {ellipsize && '...'}
        </Text>
        {body?.trim() && (
          <Text className="text-body-md text-od-content-primary md:text-body-lg [overflow-wrap:anywhere]">
            {body.trim()}
          </Text>
        )}
      </Col>
    </Col>
  );
};

const ReviewList = (_entry: ILpComponentOpendoorReviews, resolvedData: unknown) => {
  const [filterType, setFilterType] = useState<ProductType>(ProductType.sell);

  const [reviewCount, setReviewCount] = useState(10);

  const typedResolvedData = resolvedData as Awaited<ReturnType<typeof reviewsLoader>> | undefined;

  if (!typedResolvedData?.productTypeFeeds) {
    return null;
  }

  const allFeedData = typedResolvedData.productTypeFeeds[filterType || ProductType.sell];

  const explainer = productTypeToExplainer[filterType || ProductType.sell];

  const paginatedFeedData = allFeedData.slice(0, reviewCount);
  const hasMoreReviews = reviewCount < allFeedData.length;

  const selectedFilter = FILTER_OPTIONS.find((option) => option.value === filterType) ?? null;

  const renderFeedItem = (feedItem: FeedItem, key: number) => {
    switch (feedItem.type) {
      case 'review':
        return <ReviewCard key={key} review={feedItem.reviewInfo} />;

      case 'disclaimer':
        return <ThirdPartyVerification key={key} />;

      case 'reviewsio':
        return <ReviewsIoCard key={key} reviewsIo={feedItem} />;

      case 'bbb':
        return <BbbOrgCard key={key} />;

      default:
        return null;
    }
  };

  return (
    <LandingPageSectionContainer className="min-h-100">
      <Col className="gap-8">
        <Col className="gap-6 md:flex-row md:justify-between">
          <Col className="justify-between gap-4 md:w-78.75 lg:w-131.25">
            <Text as="h3" className="text-subheader-lg text-od-content-primary">
              {explainer.heading}
            </Text>
            <Text as="h3" className="text-body-lg text-od-content-primary">
              {explainer.description}
            </Text>
          </Col>
          <Col className="w-full md:w-78.75">
            <Field label="Review types" id="review-types-filter">
              <Combobox
                id="review-types-filter"
                aria-label="Review types"
                size="sm"
                options={FILTER_OPTIONS}
                value={selectedFilter}
                onValueChange={(option) => {
                  setReviewCount(10);
                  setFilterType((option?.value as ProductType) ?? ProductType.sell);
                }}
              />
            </Field>
          </Col>
        </Col>

        <Col className={cn(!hasMoreReviews && 'pb-8')}>
          <Col className="gap-8">
            {paginatedFeedData.map((feedItem: FeedItem, index: number) =>
              renderFeedItem(feedItem, index),
            )}
          </Col>

          {hasMoreReviews && (
            <Row className="w-full items-center justify-center py-4">
              <Button
                onClick={() => setReviewCount((count) => count + 10)}
                variant="primary"
                size="sm"
                analyticsName="load-more-reviews"
                aria-label="Load More"
              >
                Load More
              </Button>
            </Row>
          )}
        </Col>
      </Col>
    </LandingPageSectionContainer>
  );
};

const reviewsLoader = async () => {
  try {
    const { productTypeFeeds, stats } = await fetchReviews();
    // JSON.parse(JSON.stringify(...)) strips undefined values which can't be serialized by Next.js
    return JSON.parse(JSON.stringify({ productTypeFeeds, stats }));
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log('error occured while processing reviews', err);
    return { productTypeFeeds: { sell: [], buy: [], 'trade-in': [] }, stats: null };
  }
};

const Reviews = {
  render: ReviewList,
  loader: reviewsLoader,
};

export default Reviews;
